import { Check, Ship, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Address } from '@my/directory-api';
import { useFerryBookings, useFerryRoutes, useRefreshFerryBooking } from '@my/ferry-api/hooks';
import {
  useDeleteOrderBooking,
  useOrderTasks,
  useUpdateOrderBooking,
  useUpdateOrderTask,
} from '@my/tms-api/hooks';
import { ConfirmDialog, showToast, Switch } from '@my/ui';
import { FerryStatusStrip } from '../../ferry/components/FerryStatusStrip';
import { ferryAmendBlockingMessage, updateBodyFrom, useFerryBookingFlow } from '../../ferry/flow';
import { utcToZoned } from '../../ferry/portTime';
import { useShippingIntegration } from '../../ferry/shippingIntegration';
import { ferryBookingAccepted } from '../../ferry/status';
import {
  bookingFormValuesFromSource,
  EMPTY_BOOKING_FORM_VALUES,
} from '../lib/bookingForm';
import { addDaysISO, parseISODate, shiftInDays } from '@/utils/dates';
import type { BookingFormValues, MockBooking } from '../lib/types';
import { BookingFormFields } from './BookingFormFields';

/**
 * Edit-mode replacement for the BookingMetadataChip. The save flow has
 * two steps: PUT the booking row, then (only if either port changed)
 * PUT each of the paired BookingDeparture / BookingArrival tasks with
 * their new addressId — the booking update endpoint doesn't touch the
 * tasks' addressIds.
 */
export function BookingMetadataEditPanel({
  booking,
  orderId,
  addresses,
  organisationId,
  onClose,
}: {
  booking: MockBooking;
  orderId: string;
  /** Org-wide address catalogue — drives the port comboboxes + the map. */
  addresses: Address[];
  /** Forwarded so the Route picker can resolve per-org selections. */
  organisationId: string | null;
  onClose: () => void;
}) {
  // `_source` absent → mockup-only booking; guard the save flow so a
  // stub doesn't PUT a synthetic id.
  const source = booking._source;
  const updateBookingMutation = useUpdateOrderBooking();
  const deleteBookingMutation = useDeleteOrderBooking();
  const updateTaskMutation = useUpdateOrderTask();
  // The booking carries only a SUMMARY of its two half-tasks, and the task PUT
  // is a full replace — so the full rows are needed to echo back everything this
  // panel does not edit. Same query the order page already runs, served from
  // cache; without it the save cleared the legs' temperature, notes, reference,
  // POD and service tags.
  const orderTasksQuery = useOrderTasks(orderId);
  const fullTask = (taskId: string) => orderTasksQuery.data?.find((t) => t.id === taskId);
  // The carrier's side of the booking, found by the link Ferry holds
  // (externalReference = this booking's id) rather than by our column, so a
  // booking whose link write failed is still found — and re-linked below.
  // With the integration off it is asked only for a booking already at the
  // carrier, so that one can still be moved or cancelled there.
  const { enabled: integrationEnabled } = useShippingIntegration(organisationId);
  const askFerry = !!source && (integrationEnabled || !!source.ferryBookingId);
  const ferryQuery = useFerryBookings(askFerry ? organisationId : null, {
    externalReference: source?.id,
  });
  const ferryBooking = ferryQuery.data?.[0] ?? null;
  const ferryRoutesQuery = useFerryRoutes(ferryBooking?.carrierId ?? null);
  const ferryOriginZone =
    ferryRoutesQuery.data?.find((r) => r.id === ferryBooking?.routeId)?.originTimeZoneId ?? 'UTC';
  const refreshFerry = useRefreshFerryBooking();
  const ferryFlow = useFerryBookingFlow();
  // Default on: the legacy service always kept the block for Stena.
  const [keepCapacity, setKeepCapacity] = useState(true);

  const isBusy =
    updateBookingMutation.isPending ||
    deleteBookingMutation.isPending ||
    updateTaskMutation.isPending ||
    ferryFlow.isBusy;
  const [pendingDelete, setPendingDelete] = useState(false);

  // Kept to tell what the operator changed, and so what goes to the carrier.
  const [initialValues] = useState<BookingFormValues>(() =>
    source ? bookingFormValuesFromSource(source) : EMPTY_BOOKING_FORM_VALUES,
  );
  const [values, setValues] = useState<BookingFormValues>(initialValues);
  const onChange = (patch: Partial<BookingFormValues>) =>
    setValues((prev) => ({ ...prev, ...patch }));

  // The create's write-back failed: the link and the carrier's answer go onto
  // the row and into the form, so the locks apply and a save keeps them.
  useEffect(() => {
    if (!source || !orderId || !ferryBooking || source.ferryBookingId) return;
    const number = ferryBookingAccepted(ferryBooking)
      ? String(ferryBooking.carrierBookingNumber)
      : null;
    updateBookingMutation.mutate({
      orderId,
      body: {
        ...updateBodyFrom(source),
        ferryBookingId: ferryBooking.id,
        ...(number
          ? { carrierReference: number, vessel: ferryBooking.shipName ?? source.vessel ?? null }
          : {}),
      },
    });
    setValues((prev) => ({
      ...prev,
      ferryBookingId: ferryBooking.id,
      ...(number ? { carrierReference: number, vessel: ferryBooking.shipName ?? prev.vessel } : {}),
    }));
    // Once per found booking; the mutation object itself is not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ferryBooking?.id, source?.ferryBookingId]);

  const canSave = !!source && !!orderId && !isBusy;

  const handleSave = async () => {
    if (!canSave || !source) return;
    try {
      // Step 0 — the carrier. A change to what it holds (sailing, plates,
      // drivers) is amended at Ferry first; a refusal stops the whole save so
      // the TMS row keeps matching the carrier.
      let v = values;
      const hhmm = (t: string) => t.slice(0, 5);
      const sailingChanged =
        values.sailDate !== initialValues.sailDate ||
        hhmm(values.sailTime) !== hhmm(initialValues.sailTime);
      const unitChanged =
        values.trailerNumber.trim() !== initialValues.trailerNumber.trim() ||
        (values.isAccompanied &&
          (values.vehicleRegistration.trim() !== initialValues.vehicleRegistration.trim() ||
            values.numberOfDrivers !== initialValues.numberOfDrivers));
      if ((ferryBooking || values.ferryBookingId) && (sailingChanged || unitChanged)) {
        const ferryRoute = ferryRoutesQuery.data?.find((r) => r.id === ferryBooking?.routeId);
        if (!ferryBooking || !ferryRoute) {
          showToast({
            type: 'error',
            message: 'The carrier booking could not be read yet; try again in a moment.',
          });
          return;
        }
        const blocking = sailingChanged ? ferryAmendBlockingMessage(values) : null;
        if (blocking) {
          showToast({ type: 'error', message: blocking });
          return;
        }
        const answer = await ferryFlow.amendAtFerry({
          ferryBooking,
          routeCode: ferryRoute.code,
          zone: ferryOriginZone,
          sailingChanged,
          values,
        });
        if (answer.integrationStatus === 'Failed') {
          showToast({
            type: 'error',
            message: answer.message ?? `${booking.carrier || 'The carrier'} refused the change.`,
          });
          return;
        }
        // The TMS row records the carrier's answer: number, ship, and the
        // sailing it holds, in port time.
        const sailing = answer.scheduledDeparture
          ? utcToZoned(answer.scheduledDeparture, ferryOriginZone)
          : null;
        v = {
          ...values,
          carrierReference:
            answer.carrierBookingNumber != null
              ? String(answer.carrierBookingNumber)
              : values.carrierReference,
          vessel: answer.shipName ?? values.vessel,
          ...(sailing ? { sailDate: sailing.date, sailTime: sailing.time } : {}),
        };
      }

      // Step 1 — booking metadata. The Route picker in
      // BookingFormFields writes carrierRouteId (OrganisationRoute.id)
      // plus the denormalised routeCode / routeName / typical-duration
      // snapshot into `values`, so we persist those straight through.
      // Empty string → null so the BE clears the FK rather than
      // rejecting an empty Guid string.
      await updateBookingMutation.mutateAsync({
        orderId,
        body: {
          bookingId: source.id,
          carrierPartyId: v.carrierPartyId || null,
          agentPartyId: v.agentPartyId || null,
          carrierRouteId: v.carrierRouteId || null,
          routeCode: v.routeCode.trim() || null,
          routeName: v.routeName.trim() || null,
          typicalDurationMinutes: v.typicalDurationMinutes,
          typicalCheckInMinutes: v.typicalCheckInMinutes,
          typicalDriveOffMinutes: v.typicalDriveOffMinutes,
          bookingType: v.bookingType,
          isAccompanied: v.isAccompanied,
          carrierReference: v.carrierReference.trim() || null,
          vessel: v.vessel.trim() || null,
          voyageNumber: v.voyageNumber.trim() || null,
          sailDate: v.sailDate || null,
          sailTime: v.sailTime || null,
          numberOfDrivers: v.isAccompanied ? Number.parseInt(v.numberOfDrivers, 10) || null : null,
          vehicleRegistration: v.isAccompanied ? v.vehicleRegistration.trim() || null : null,
          trailerNumber: v.trailerNumber.trim() || null,
          customsReference: v.customsReference.trim() || null,
          clearanceStatus: v.clearanceStatus === '' ? null : v.clearanceStatus,
          notes: v.notes.trim() || null,
          // The PUT replaces the whole row; without this every save dropped the link.
          ferryBookingId: ferryBooking?.id ?? (v.ferryBookingId || null),
        },
      });

      // Step 2 — the paired tasks. The booking PUT deliberately leaves them
      // alone (see UpdateOrderBookingHandler), so anything the two surfaces
      // SHARE has to be pushed here or the task rows go stale — which is
      // exactly what happened with the sail date: the booking moved, the two
      // half-tasks kept the old planned date, and the timeline went on showing
      // the crossing on the original day with an out-of-order warning.
      const departureTask = source.tasks.find((t) => t.type === 'BookingDeparture');
      const arrivalTask = source.tasks.find((t) => t.type === 'BookingArrival');

      const sailDateChanged = !!v.sailDate && v.sailDate !== (source.sailDate ?? '');
      const sailTimeChanged = (v.sailTime || null) !== (source.sailTime ?? null);

      // The BOARDING half's date IS the sail date — no offset, ever. An earlier
      // version of this shifted each half by however many days the sailing had
      // moved, meaning to preserve a deliberate overnight arrival. It measured
      // that offset against the SAIL DATE, so any pre-existing wrong date was
      // preserved instead of corrected: a booking whose halves sat five days
      // before the sailing kept sitting five days before it, for ever, however
      // often the sailing was rescheduled.
      //
      // The overnight-ness belongs to the CROSSING (departure → arrival), not to
      // the gap between a task and the sailing. So that span is what carries
      // over, floored at zero — a disembark cannot precede its own boarding, and
      // a negative span is bad data rather than an instruction.
      const crossingSpan = Math.max(
        0,
        shiftInDays(departureTask?.plannedDate, arrivalTask?.plannedDate) ?? 0,
      );

      const taskUpdates: Array<Promise<unknown>> = [];
      const departureAddressChanged =
        !!v.departureAddressId && departureTask?.addressId !== v.departureAddressId;
      if (departureTask && (departureAddressChanged || sailDateChanged || sailTimeChanged)) {
        taskUpdates.push(
          updateTaskMutation.mutateAsync({
            orderId,
            body: {
              taskId: departureTask.id,
              sequence: departureTask.sequence,
              addressId: v.departureAddressId || departureTask.addressId,
              state: departureTask.state,
              plannedDate: sailDateChanged
                ? v.sailDate || null
                : departureTask.plannedDate ?? null,
              // The boarding half's time IS the sail time — that is how create
              // sets it, so a changed sailing has to bring it along.
              plannedTime: sailTimeChanged
                ? v.sailTime || null
                : departureTask.plannedTime ?? null,
              plannedDepartureTime: departureTask.plannedDepartureTime ?? null,
              // Echoed from the task, not hard-coded. This panel edits the
              // sailing and the ports; the PUT replaces the whole task, so the
              // nulls that used to sit here cleared the leg's temperature, notes,
              // reference, POD and service tags on every booking edit.
              temperatureC: fullTask(departureTask.id)?.temperatureC ?? null,
              timeMode: fullTask(departureTask.id)?.timeMode ?? null,
              notes: fullTask(departureTask.id)?.notes ?? null,
              actualDate: fullTask(departureTask.id)?.actualDate ?? null,
              actualArrivalTime: fullTask(departureTask.id)?.actualArrivalTime ?? null,
              actualDepartureTime: fullTask(departureTask.id)?.actualDepartureTime ?? null,
              statusText: fullTask(departureTask.id)?.statusText ?? null,
              taskReference: fullTask(departureTask.id)?.taskReference ?? null,
              podStatus: fullTask(departureTask.id)?.podStatus ?? null,
              podSignedAt: fullTask(departureTask.id)?.podSignedAt ?? null,
              podSignedBy: fullTask(departureTask.id)?.podSignedBy ?? null,
              serviceTagIds: fullTask(departureTask.id)?.serviceTagIds ?? [],
            },
          }),
        );
      }
      const arrivalAddressChanged =
        !!v.arrivalAddressId && arrivalTask?.addressId !== v.arrivalAddressId;
      if (arrivalTask && (arrivalAddressChanged || sailDateChanged)) {
        taskUpdates.push(
          updateTaskMutation.mutateAsync({
            orderId,
            body: {
              taskId: arrivalTask.id,
              sequence: arrivalTask.sequence,
              addressId: v.arrivalAddressId || arrivalTask.addressId,
              state: arrivalTask.state,
              plannedDate: sailDateChanged
                ? v.sailDate
                  ? addDaysISO(parseISODate(v.sailDate), crossingSpan)
                  : null
                : arrivalTask.plannedDate ?? null,
              plannedTime: arrivalTask.plannedTime ?? null,
              plannedDepartureTime: arrivalTask.plannedDepartureTime ?? null,
              // Echoed from the task, not hard-coded. This panel edits the
              // sailing and the ports; the PUT replaces the whole task, so the
              // nulls that used to sit here cleared the leg's temperature, notes,
              // reference, POD and service tags on every booking edit.
              temperatureC: fullTask(arrivalTask.id)?.temperatureC ?? null,
              timeMode: fullTask(arrivalTask.id)?.timeMode ?? null,
              notes: fullTask(arrivalTask.id)?.notes ?? null,
              actualDate: fullTask(arrivalTask.id)?.actualDate ?? null,
              actualArrivalTime: fullTask(arrivalTask.id)?.actualArrivalTime ?? null,
              actualDepartureTime: fullTask(arrivalTask.id)?.actualDepartureTime ?? null,
              statusText: fullTask(arrivalTask.id)?.statusText ?? null,
              taskReference: fullTask(arrivalTask.id)?.taskReference ?? null,
              podStatus: fullTask(arrivalTask.id)?.podStatus ?? null,
              podSignedAt: fullTask(arrivalTask.id)?.podSignedAt ?? null,
              podSignedBy: fullTask(arrivalTask.id)?.podSignedBy ?? null,
              serviceTagIds: fullTask(arrivalTask.id)?.serviceTagIds ?? [],
            },
          }),
        );
      }
      await Promise.all(taskUpdates);

      showToast({ type: 'success', message: 'Booking updated.' });
      onClose();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to update booking.',
      });
    }
  };

  const ferryAccepted = !!ferryBooking && ferryBookingAccepted(ferryBooking);

  const confirmDelete = async () => {
    if (!source || !orderId) return;
    try {
      // A linked booking whose carrier side could not be read is not deleted blind.
      if (source.ferryBookingId && !ferryBooking) {
        showToast({
          type: 'error',
          message: 'The carrier booking could not be read; try again before deleting.',
        });
        return;
      }
      // The carrier first; if it refuses, nothing is deleted.
      if (ferryBooking) {
        const refusal = await ferryFlow.cancelAtFerry({ ferryBooking, keepCapacity });
        if (refusal) {
          showToast({ type: 'error', message: `${refusal} The booking was kept.` });
          return;
        }
      }
      await deleteBookingMutation.mutateAsync({ orderId, bookingId: source.id });
      showToast({
        type: 'success',
        message: ferryAccepted ? `Cancelled with ${booking.carrier} and deleted.` : 'Booking deleted.',
      });
      onClose();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete booking.',
      });
    } finally {
      setPendingDelete(false);
    }
  };

  return (
    <div className="rounded-md border-2 border-purple-300 bg-purple-50/40 p-3">
      <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-purple-700">
        <Ship className="h-3.5 w-3.5" />
        Editing booking — changes apply to both halves
      </div>

      {ferryBooking && (
        <FerryStatusStrip
          ferryBooking={ferryBooking}
          carrierName={booking.carrier || 'Carrier'}
          originZone={ferryOriginZone}
          onRefresh={() => refreshFerry.mutate(ferryBooking.id)}
          refreshing={refreshFerry.isPending}
        />
      )}

      <BookingFormFields
        values={values}
        onChange={onChange}
        addresses={addresses}
        organisationId={organisationId}
        disabled={isBusy}
      />

      {/* Action bar is confined to the form column (3fr) so it ends
          before the map starts — matching the pickup / delivery task
          layout. Delete sits alone on the left (destructive); Cancel /
          Save on the right. Delete removes the booking AND both paired
          tasks atomically server-side. */}
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label="Delete booking"
            onClick={() => setPendingDelete(true)}
            disabled={isBusy || !source || ferryQuery.isLoading}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Cancel"
              onClick={onClose}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Save booking"
              onClick={handleSave}
              disabled={!canSave}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete}
        title="Delete booking?"
        message={
          ferryAccepted ? (
            <div className="space-y-3">
              <p>
                The booking is cancelled with {booking.carrier} first, then removed with both
                paired tasks (Boarding and Disembark). This can't be undone.
              </p>
              <Switch
                label="Keep the space at the carrier"
                enabledLabel="Yes, keep the block"
                disabledLabel="No, release it"
                checked={keepCapacity}
                onChange={(e) => setKeepCapacity(e.target.checked)}
              />
            </div>
          ) : (
            "The booking and both paired tasks (Boarding and Disembark) will be removed. This can't be undone."
          )
        }
        confirmLabel="Delete"
        tone="danger"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(false)}
        busy={deleteBookingMutation.isPending || ferryFlow.isBusy}
      />
    </div>
  );
}
